import React from 'react';
import { PageHero } from '@/components/shared/Section';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { submitContact } from '@/lib/api';
import { toast } from 'sonner';
import { CheckCircle2, Mail, MapPin, Phone, ArrowUpRight } from 'lucide-react';
import { CONTACT } from '@/constants/testIds';
import { SERVICES } from '@/data/content';

export default function ContactPage() {
  const [form, setForm] = React.useState({
    name: '', email: '', phone: '', company: '', service: '', message: '',
  });
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      if (!payload.company) delete payload.company;
      if (!payload.service) delete payload.service;
      await submitContact(payload);
      setSuccess(true);
      toast.success('Message sent. We will reach out within 24 hours.');
      setForm({ name: '', email: '', phone: '', company: '', service: '', message: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact us"
        title="Let's grow your business together."
        description="Whether you need business support, workforce solutions, recruitment assistance or corporate services — we are ready to help."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 space-y-4">
            <div className="rounded-sm border border-slate-200 bg-white p-7">
              <div className="eyebrow text-tito-blue/70">Reach us directly</div>
              <ul className="mt-5 space-y-5">
                <li className="flex items-start gap-3">
                  <span className="h-10 w-10 inline-flex items-center justify-center rounded-sm bg-blue-50 text-tito-blue">
                    <Phone className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Phone</div>
                    <a href="tel:+971558805477" className="font-medium text-slate-900 hover:text-tito-blue">+971 55 880 5477</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-10 w-10 inline-flex items-center justify-center rounded-sm bg-blue-50 text-tito-blue">
                    <Mail className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Email</div>
                    <a href="mailto:titocorporateserviceprovider@gmail.com" className="font-medium text-slate-900 hover:text-tito-blue break-all">
                      titocorporateserviceprovider@gmail.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-10 w-10 inline-flex items-center justify-center rounded-sm bg-blue-50 text-tito-blue">
                    <MapPin className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Office</div>
                    <p className="font-medium text-slate-900 leading-snug">101, Ibrahim Hussain Ahli Tower, Muteena, Deira, Dubai</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="rounded-sm bg-[#003B73] text-white p-7">
              <div className="eyebrow text-tito-gold">Response promise</div>
              <p className="mt-3 text-sm text-blue-100/90 leading-relaxed">
                We respond to every enquiry within one business day. For urgent recruitment or corporate-services matters, call us directly.
              </p>
            </div>
          </aside>

          <div className="lg:col-span-8">
            {success ? (
              <div data-testid={CONTACT.success} className="rounded-sm border border-slate-200 bg-white p-10 md:p-14 text-center">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-sm bg-tito-gold/15 text-tito-gold">
                  <CheckCircle2 className="h-7 w-7" />
                </div>
                <h3 className="mt-6 font-display font-bold text-2xl sm:text-3xl text-slate-900">Thank you — message received.</h3>
                <p className="mt-3 text-slate-600 max-w-md mx-auto">A member of our team will get back to you within one business day.</p>
                <Button onClick={() => setSuccess(false)} className="mt-8 rounded-sm bg-tito-blue hover:bg-blue-900 text-white">
                  Send another message
                </Button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid={CONTACT.form}
                className="rounded-sm border border-slate-200 bg-white p-7 md:p-10"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name *">
                    <Input required value={form.name} onChange={set('name')} data-testid={CONTACT.name} placeholder="Jane Doe" />
                  </Field>
                  <Field label="Email *">
                    <Input required type="email" value={form.email} onChange={set('email')} data-testid={CONTACT.email} placeholder="you@company.com" />
                  </Field>
                  <Field label="Phone">
                    <Input value={form.phone} onChange={set('phone')} data-testid={CONTACT.phone} placeholder="+971 ..." />
                  </Field>
                  <Field label="Company">
                    <Input value={form.company} onChange={set('company')} data-testid={CONTACT.company} placeholder="Your company" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">
                      Service of interest
                    </Label>
                    <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                      <SelectTrigger data-testid={CONTACT.service} className="mt-2 rounded-sm h-11">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICES.map((s) => (
                          <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>
                        ))}
                        <SelectItem value="General Enquiry">General Enquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="How can we help? *">
                      <Textarea
                        required
                        rows={5}
                        value={form.message}
                        onChange={set('message')}
                        data-testid={CONTACT.message}
                        placeholder="Tell us a little about your business and what you need..."
                        className="resize-none"
                      />
                    </Field>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid={CONTACT.submit}
                  className="mt-8 rounded-sm bg-tito-blue hover:bg-blue-900 text-white h-12 px-7 gap-2 font-semibold"
                >
                  {loading ? 'Sending…' : 'Send Message'}
                  <ArrowUpRight className="h-4 w-4" />
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);
